/**
 * Utility class for string operations
 */
export class WizStringUtils {

  /** suppression des sauts / fin de ligne */
  public static eraseNR(str:string) :string {
    return str.replace(/(\r\n|\n|\r)/gm, '');
  }

  /**
   * <p>Checks if a String is empty ("") or null.</p>
   *
   *  <pre>
   *  StringUtils.isEmpty(null)      = true
   *  StringUtils.isEmpty("")        = true
   *  StringUtils.isEmpty(" ")       = false
   *  StringUtils.isEmpty("bob")     = false
   *  StringUtils.isEmpty("  bob  ") = false
   * </pre>
   *
   * @param pStr the String to check, may be null
   * @return <code>true</code> if the String is empty or null
   */
  public static isEmpty(pStr: string) {
    return pStr === null || pStr === undefined || pStr.length === 0;
  }

  /**
   * <p>Checks if a String is not empty ("not") or null.</p>
   *
   *  <pre>
   *  StringUtils.isNotEmpty(null)      = false
   *  StringUtils.isNotEmpty("")        = false
   *  StringUtils.isNotEmpty(" ")       = true
   *  StringUtils.isNotEmpty("bob")     = true
   *  StringUtils.isNotEmpty("  bob  ") = true
   * </pre>
   * @param pStr the String to check, may be null
   */
  public static isNotEmpty(pStr: string) {
    return !this.isEmpty(pStr);
  }

  /**
   * <p>Checks if the string is a blank (" ").</p>
   *
   *  <pre>
   *  StringUtils.isBlank(null)      = true
   *  StringUtils.isBlank("")        = true
   *  StringUtils.isBlank(" ")       = true
   *  StringUtils.isBlank("bob")     = false
   *  StringUtils.isBlank("  bob  ") = false
   * </pre>
   * @param pStr the String to check, may be null
   */
  public static isBlank(pStr: string) {
    if (pStr === null || (pStr.length) === 0) {
        return true;
    }
    for (const elm of pStr) {
      if (elm !== '' && elm !== ' ' && elm !== '\n' && elm !== '\t') {
        return false;
      }
    }
    return true;
  }

  /**
   * <p>Check if the string is a blank (" ").</p>
   *
   *  <pre>
   *  StringUtils.isBlank(null)         = false
   *  StringUtils.isNotBlank("")        = false
   *  StringUtils.isNotBlank(" ")       = false
   *  StringUtils.isNotBlank("bob")     = true
   *  StringUtils.isNotBlank("  bob  ") = true
   * </pre>
   * @param pStr the String to check, may be null
   */
  public static isNotBlank(pStr: string) {
    return !this.isBlank(pStr);
  }

  /**
   * <p>Function that allows to format an error message.</p>
   *
   *  <pre>
   *   StringUtils.messageFormat('an error message', 'the text is short')  = 'an error message\nthe text is short'
   *  </pre>
   * @param pVarMsg the source message.
   * @param pMsg the message to add.
   */
  public static messageFormat(pVarMsg: string, pMsg: string): string {
    return (pVarMsg !== '') ? pVarMsg + '\n' + pMsg : pMsg;

  }

  /**
   * <p>return the string after the first occurence of pChar.</p>
   *
   *  <pre>
   *  StringUtils.afterFirst(null, _)      = ''
   *  StringUtils.afterFirst("", _)        = ''
   *  StringUtils.afterFirst(" ", _)       = ''
   *  StringUtils.afterFirst("bob", "a")   = ''
   *  StringUtils.afterFirst("bob", "b")   = 'ob'
   * </pre>
   * @param pString le texte en entier
   * @param pChar la lettre de repère
   */
  public static afterFirst(pString: string, pChar: string): string {
    if (pString === null) {
      return '';
    }
    let idx = pString.indexOf(pChar);
    if (idx === -1) {
      return '';
    }
    idx += pChar.length;
    return pString.substr(idx);
  }

  /**
   * <p>return the string after the last occurence of pChar.</p>
   *
   *  <pre>
   *  StringUtils.afterFirst(null, _)      = ''
   *  StringUtils.afterFirst("", _)        = ''
   *  StringUtils.afterFirst(" ", _)       = ''
   *  StringUtils.afterFirst("bob", "a")     = ''
   *  StringUtils.afterFirst("bob", "b") = ''
   *  StringUtils.afterFirst("babouche", "b") = 'ouche'
   * </pre>
   * @param pString le texte en entier
   * @param pChar la lettre de repère
   */
  public static afterLast(pString: string, pChar: string): string {
    if (pString === null) {
      return '';
    }
    let idx = pString.lastIndexOf(pChar);
    if (idx === -1) {
      return '';
    }
    idx += pChar.length;
    return pString.substring(idx);
  }


  /**
   * <p>return the string before the first occurence of pChar.</p>
   *
   *  <pre>
   *  StringUtils.afterFirst(null, _)      = ''
   *  StringUtils.afterFirst("", _)        = ''
   *  StringUtils.afterFirst(" ", _)       = ''
   *  StringUtils.afterFirst("bob", "a")     = ''
   *  StringUtils.afterFirst("bob", "b") = ''
   *  StringUtils.afterFirst("babouche", "o") = 'bab'
   * </pre>
   * @param pString the entire text
   * @param pChar the limit character
   */
  public static beforeFirst(pString: string, pChar: string): string {
    if (pString === null) {
      return '';
    }
    const idx = pString.indexOf(pChar);
    if (idx === -1) {
      return '';
    }
    return pString.substring(0, idx);
  }

  /**
   * <p>return the string before the first occurence of pChar.</p>
   *
   *  <pre>
   *  StringUtils.beforeLast(null, _)      = ''
   *  StringUtils.beforeLast("", _)        = ''
   *  StringUtils.beforeLast(" ", _)       = ''
   *  StringUtils.beforeLast("bob", "a")     = ''
   *  StringUtils.beforeLast("bob", "b") = ''
   *  StringUtils.beforeLast("babouche", "b") = 'ba'
   * </pre>
   * @param pString the entire text
   * @param pChar the limit character
   */
  public static beforeLast(pString: string, pChar: string): string {
    if (pString === null) {
      return '';
    }
    const idx = pString.lastIndexOf(pChar);
    if (idx === -1) {
      return '';
    }
    return pString.substring(0, idx);
  }

  /**
   * <p>return a capitalized string of the gived string.</p>
   *
   *  <pre>
   *  StringUtils.capitalize('')      = ''
   *  StringUtils.capitalize('bob')      = 'Bob'
   *  <pre>
   * @param pString the string to capitalize
   */
  public static capitalize(pString: string ) {
    return pString.charAt(0).toUpperCase() + pString.slice(1);
  }

  /**
   * <p>Check if the given string contains the uppercase character.</p>
   *
   *  <pre>
   *  StringUtils.containUpper('', _)      = false
   *  StringUtils.containUpper('bob', '')      = false
   *  StringUtils.containUpper('bOb', 'o')      = true
   *  <pre>
   * @param pString the String to check, may be null
   * @param pChar the character
   */
  public static containUpper(pSource: string, pSearch: string): boolean {
    if (pSearch === null || pSearch === '' || pSearch === undefined) {
      return false;
    }
    if (pSource === null || pSource === '' || pSearch === undefined) {
      return false;
    }
    return pSource.toUpperCase().indexOf(pSearch.toUpperCase()) !== -1;
  }


  /**
   * <p>Check if the given string contains the uppercase character.</p>
   *
   *  <pre>
   *  StringUtils.countOf(null, _, _)      = 0
   *  StringUtils.countOf( _, null, _)      = 0
   *  StringUtils.countOf('bob', 'b')      = 2
   *  <pre>
   * @param pString the string
   * @param pChar the character to count
   * @param pCaseSensitive (optional, default is true) A boolean flag to indicate if the
   * 	search is case sensitive.
   */
  public static countOf(pString: string, pChar: string, pCaseSensitive: boolean = true): number {
    if (pString === null || pChar === null ) {
      return 0;
    }
    let count = 0;
    for (const char of pString) {
      if (!pCaseSensitive) {
        if (char.toLowerCase() === pChar.toLowerCase()) {
          count += 1;
        }
      } else {
        if (char === pChar) {
          count += 1;
        }
      }
    }
    return count;
  }

  /**
   * <p>Levenshtein distance (editDistance) is a measure of the similarity between two strings,
   * The distance is the number of deletions, insertions, or substitutions required to
   * 	transform pSource into pTarget..</p>
   *
   *  <pre>
   *  StringUtils.editDistance( '', 'i')      = 1
   *  StringUtils.editDistance( 'bob', '')      = 3
   *  StringUtils.editDistance('bob', 'b')      = 2
   *  <pre>
   * @param pSource  The source string
   * @param pTarget  The target string
   */
  public static editDistance(pSource: string, pTarget: string) {

    if (pSource.length === 0) { return pTarget.length; }
    if (pTarget.length === 0) { return pSource.length; }

    const matrix = [];

    // increment along the first column of each row
    let i;
    for (i = 0; i <= pTarget.length; i++) {
      matrix[i] = [i];
    }

    // increment each column in the first row
    let j;
    for (j = 0; j <= pSource.length; j++) {
      matrix[0][j] = j;
    }

    // Fill in the rest of the matrix
    for (i = 1; i <= pTarget.length; i++) {
      for (j = 1; j <= pSource.length; j++) {

        if (pTarget.charAt(i - 1) === pSource.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, // substitution
                                  Math.min(matrix[i][j - 1] + 1, // insertion
                                           matrix[i - 1][j] + 1)); // deletion
        }
      }
    }
    return matrix[pTarget.length][pSource.length];
  }

  /**
   * <p>Determines whether the specified string contains text.</p>
   *
   *  <pre>
   *  StringUtils.hasText('')      = false
   *  StringUtils.hasText('  ')      = false
   *  StringUtils.hasText('bob')      = true
   *  <pre>
   * @param pString The string to check.
   */
  public static hasText(pString: string): boolean {
    if (pString === null) {
      return null;
    }
    const str = pString.trim();
    return str.length !== 0;
  }

  /**
   * <p>Determines whether the specified string is numeric.</p>
   *
   *  <pre>
   *  StringUtils.isNumeric('')      = false
   *  StringUtils.isNumeric('bob')      = false
   *  StringUtils.isNumeric('123')      = true
   *  <pre>
   * @param pString the string.
   */
  public static isNumeric(pString: string): boolean {
    const res = parseInt(pString, 0);
    return !isNaN(res);
  }

  /**
   * <p>Determines whether the specified string is numeric.</p>
   *
   *  <pre>
   *  StringUtils.isNumeric('bob')      = false
   *  StringUtils.isNumeric('a@b.d')      = false
   *  StringUtils.isNumeric('a@b.cd')      = true, 2 lettre après le point au minimum
   *  <pre>
   * @param pString the string
   */
  public static isMail(pString: string) {
    if (pString === null || pString === '') { return false; }
    if (pString) {
      const mailRegExp = new RegExp(/([0-9a-zA-Z]+[-._+&])*[0-9a-zA-Z]+@([-0-9a-zA-Z]+[.])+[a-zA-Z]{2,6}/);
      return mailRegExp.test(pString);
    }
    return false;
  }


  /**
   * cette methode existe en js sous string.padStart()
   * <p>Pads pString with specified character to a specified length from the left.</p>
   *
   *  <pre>
   *  StringUtils.padLeft('bob', '.','2')      = 'bob'
   *  StringUtils.padLeft('bob', '.','5')      = '..bob'
   *  <pre>
   * @param pString the string to pad
   * @param pPadChar the character for pad
   * @param pLength the length to pad
   */
  public static padLeft(pString: string, pPadChar: string, pLength: number): string {
    let s = pString;
    while (s.length < pLength) {
      s = pPadChar + s;
    }
    return s;
  }


  /**
   * cette methode existe en js sous string.quote() mais est obsolète
   * <p>	Escapes all of the characters in a string to create a 'quotable' string.</p>
   *
   *  <pre>
   *  StringUtils.quote('bob')      = '"bob"'
   *  <pre>
   * @param pString the string
   */
  public static quote(pString: string): string {
    const regx = new RegExp(/[\\"\r\n]/g);
    return '"' + pString.replace(regx, this._quote) + '"';
  }


  /**
   * <p>Removes all instances of the remove string in the input string.</p>
   *
   *  <pre>
   *  StringUtils.remove('', _, _)          = ''
   *  StringUtils.remove('bob', 'b', true)  = 'o'
   *  StringUtils.remove('Bob', 'b', true)  = 'Bo'
   *  StringUtils.remove('BoB', 'b', false)  = 'o'
   *  <pre>
   * @param pString The string that will be checked for instances of remove string.
   * @param pRemove The string that will be removed from the input string.
   * @param pCaseSensitive An optional boolean indicating if the replace is case sensitive. Default is true.
   */
  public static remove(pString: string, pRemove: string, pCaseSensitive: boolean = true): string {
    if (pString === null || pString === '') {
      return '';
    }
    const rem = pRemove.replace(/(\]|\[|\{|\}|\(|\)|\*|\+|\?|\.|\\)/g, '\\$1');
    const flags = (!pCaseSensitive) ? 'ig' : 'g';
    return pString.replace(new RegExp(rem, flags), '');
  }

  /**
   * <p>Removes extraneous whitespace (extra spaces, tabs, line breaks, etc) from the
   * specified string.</p>
   *
   *  <pre>
   *  StringUtils.removeExtraWhitespace('')     = ''
   *  StringUtils.removeExtraWhitespace('  bob  ')  = 'bob'
   *  StringUtils.removeExtraWhitespace('  bob     bob    ')  = 'bob bob'
   *  <pre>
   * @param pString The String whose extraneous whitespace will be removed.
   */
  public static removeExtraWhitespace(pString: string): string {
    if (pString === null || pString === '') {
      return '';
    }
    const str = pString.trim();
    return str.replace(/\s+/g, ' ');
  }

  /**
   * <p> Properly cases' the string in 'sentence format'.</p>
   *
   *  <pre>
   *  StringUtils.removeExtraWhitespace('')     = ''
   *  StringUtils.removeExtraWhitespace('a simple text')  = 'A simple text'
   *  <pre>
   * @param pString  the string
   */
  public static  properCase(pString: string): string {
    if (pString == null) {
      return '';
    }
    const str = pString.toLowerCase().replace(/\b([^.?;!]+)/, this.capitalize);
    return str.replace(/\b[i]\b/, 'I');
  }

  /**
   * <p>Returns the specified string in reverse character order.</p>
   *
   *  <pre>
   *  StringUtils.reverse(null)     = ''
   *  StringUtils.reverse('abcd')  = 'dcba'
   *  <pre>
   * @param pString the string
   */
  public static reverse(pString: string): string {
    if (pString == null) {
      return '';
    }
    return pString.split('').reverse().join('');
  }

  /**
   * <p>Returns the specified string in reverse word order.</p>
   *
   *  <pre>
   *  StringUtils.reverseWords(null)     = ''
   *  StringUtils.reverseWords('abcd efgh')  = 'efgh abcd'
   *  <pre>
   * @param pString the text to reverse
   */
  public static reverseWords(pString: string): string {
    if (pString == null) {
      return '';
    }
    return pString.split(/\s+/).reverse().join(' ');
  }



  /**
   * <p>Determines the percentage of similiarity, based on editDistance.</p>
   *
   *  <pre>
   *  StringUtils.similarity('bob', 'bob')    = 100
   *  StringUtils.similarity('abcd', 'efgh')  = 0
   *  StringUtils.similarity('abcd', 'ab')    = 50
   *  StringUtils.similarity('bobo', 'boba')  = 75
   *  <pre>
   * @param pSource the source string
   * @param pTarget the target string
   */
  public static similarity(pSource: string, pTarget: string): number {
    const ed = this.editDistance(pSource, pTarget);
    const maxLen = Math.max(pSource.length, pTarget.length);
    if (maxLen === 0) {
      return 100;
    } else {
      return (1 - ed / maxLen) * 100;
    }
  }


  /**
   * <p>Remove's all and based tags from a string.</p>
   *
   *  <pre>
   *  StringUtils.stripTags(<p>a simple text</p>)  = 'a simple text'
   *  <pre>
   * @param pString the source string.
   */
  public static stripTags(pString: string): string {
    if (pString == null || pString === '') {
      return '';
    }
    return pString.replace(/<\/?[^>]+>/igm, '');
  }


  /**
   * <p>Swaps the casing of a string.</p>
   *
   *  <pre>
   *  StringUtils.swapCase('swapCase')  = 'SwapCase'
   *  StringUtils.swapCase('swap case')  = 'Swap case'
   *  <pre>
   * @param pString the string to swap
   */
  public static swapCase(pString: string): string {
    if (pString === null) {
      return '';
    }
    return pString.replace(/(\w)/, this._swapCase);
  }


  /**
   * <p>Determins the number of words in a string.</p>
   *
   *  <pre>
   *  StringUtils.wordCount('a simple text')  = 3
   *  <pre>
   * @param pString the string
   */
  public static wordCount(pString: string): number {
    if (pString === null || pString === '') {
      return 0;
    }
    return pString.match(/\b\w+\b/g).length;
  }

  public static  trimLeft(pString: string): string {
    if (pString == null) { return ''; }
    return pString.replace(/^\s+/, '');
  }

  public static trimRight(pString: string): string {
    if (pString == null) { return ''; }
    return pString.replace(/\s+$/, '');
  }

  /**
   * <p>Returns a string truncated to a specified length with optional suffix.</p>
   *
   *  <pre>
   *  StringUtils.truncate('truncate a text', 0, '...', true)  = 'truncate a...'
   *  StringUtils.truncate('truncate a text', 0, '...', false)  = 'truncate a t...'
   *
   *  StringUtils.truncate('truncate', 0, '...', true)  = '........'
   *  StringUtils.truncate('truncate', 0, '...', false)  = '........'
   *
   *  StringUtils.truncate('truncate', 8, '...', true)  = '...'
   *  StringUtils.truncate('truncate', 8, '...', false)  = 'trunc...'
   *  <pre>
   * @param pString the string
   * @param pLength The length the string should be shortend to.
   * @param pSuffix (optional, default=...) The string to append to the end of the truncated string.
   * @param pTrim Le comportement de la methode est bugée, passée à false,
   *                 ce paramétre permet de contourner la partie bugée.
   */
  public static truncate(pString: string, pLength: number, pSuffix: string = '...', pTrim: boolean = true): string {
    if (pString == null) {
      return '';
    }
    if (pLength < 1) {
      pLength = pString.length;
    }
    pLength -= pSuffix.length;
    let trunc = pString;
    if (trunc.length > pLength) {
      trunc = trunc.substr(0, pLength);
      if (pTrim && /[^\s]/.test(pString.charAt(pLength))) {
        trunc = trunc.replace(/\w+$|\s+$/, '');
        trunc = this.trimRight(trunc);
      }
      trunc += pSuffix;
    }
    return trunc;
  }

  /**
   * <p>Determins the alphanumeric of pNum.</p>
   *
   *  <pre>
   *  StringUtils.isBaseChar(47)  = '', same result fot any n < 47
   *
   *  StringUtils.isBaseChar(48)  = '0'
   *  StringUtils.isBaseChar(57)  = '9'
   *
   *  StringUtils.isBaseChar(48)  = 'A'
   *  StringUtils.isBaseChar(57)  = 'Z'
   *
   *  StringUtils.isBaseChar(48)  = 'a'
   *  StringUtils.isBaseChar(57)  = 'z'
   *  <pre>
   * @param pNum the number
   */
  public static isBaseChar(pNum: number): string  {
    if (pNum > 47 && pNum < 58) {
      const strNums = '0123456789';
      return strNums.charAt(pNum - 48);
    } else {
      if (pNum > 64 && pNum < 91) {
        const strCaps = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        return strCaps.charAt(pNum - 65);
      } else {
        if (pNum > 96 && pNum < 123) {
          const strLow = 'abcdefghijklmnopqrstuvwxyz';
          return strLow.charAt(pNum - 97);
        } else {
          return '';
        }
      }
    }
  }



  /**
   *  <p>simplified replace function for a sub string without the regular expressions.</p>
   *
   *  <pre>
   *   StringUtils.replace('a simple text', simple, short)  = 'a short text'
   *  <pre>
   * @param pInput the text to transform
   * @param pReplace the sub string to replace
   * @param pReplaceWith the string to replace with
   */
  // public static replace(pInput: string, pReplace: string, pReplaceWith: string): string {
  //
  //   if (pInput == null) {
  //     return pInput;
  //   }
  //   let sb = '';
  //   let found = false;
  //
  //   const sLen = pInput.length;
  //   const rLen = pReplace.length;
  //
  //   for (let i = 0; i < sLen; i++) {
  //
  //       if (pInput.charAt(i) === pReplace.charAt(0)) {
  //         found = true;
  //         for (let j = 0; j < rLen; j++) {
  //
  //           if (!(pInput.charAt(i + j) === pReplace.charAt(j))) {
  //
  //             found = false;
  //             break;
  //           }
  //         }
  //
  //         if (found) {
  //           sb += pReplaceWith;
  //           i = i + (rLen - 1);
  //           continue;
  //         }
  //       }
  //       sb += pInput.charAt(i);
  //     }
  //   return sb;
  // }

  /**
   * Remplace des caractères dans une chaine
   * Cette fonction utilise le string.replace en y ajoutant les contrôles de nullité
   * @param string: String. chaine qui dois etre remplacé
   * @param re: RegExp | string. La chaine qui dois etre remplacée
   * @param substr .La chaine qui remplace
   * @return string modifié
   */
  public static replace(string: string , re: RegExp | string, substr : string): string{
    if(!string || string === undefined){
      return null;
    }

    return string.replace(re, substr);
  }

   /**
    *  <p>add blank to string.</p>
    *
    * <pre>
    * StringUtils.addWhiteSpace('text', 8, true)  = 'text     ' ,  +4 ' '
    * StringUtils.addWhiteSpace('text', 8, true)  = '    text' ,  +4 ' '
    * <pre>
    *   @param :str : string à remplir
    *  @param :size: taille à obtenir
    *   @param :right:boolean =true remplir à droite
    *  @return chaine modifiée
    */

  public static addWhiteSpace(pStr: string, pSize: number = 15, pRight: boolean = true): string {
     let res = '';
     if (pStr) {
         res = pStr;
      }
     if (res.length > pSize) {
        return res;
      }
     while (res.length < pSize) {
       if (pRight) {
         res += ' ';
        } else {
          res = ' ' + res;
        }
      }
     return res;
  }

  /**
   * String conversion to camel case
   * @param pString string to converter
   * @param pSeparator separator
   */
  public static toCamelCase(pString: string, pSeparator: string): string {
     let result = '';
     let upNext = false;

     for (let i = 0; i < pString.length; i++) {
       let current = pString.charAt(i);
       if (current === pSeparator) {
         upNext = true;
         continue;
        }
       if (upNext) {
          upNext = false;
          current = current.toUpperCase();
        }
       result += current;
      }
     return result;
  }


  // fonctions suppementaires


  /**
   * Swap uppercase/lowercase d'une chaîne de caractères
   * @param pChar Chaîne à modifier
   */
  private static _swapCase(pChar: string): string {
    const upP = pChar.toUpperCase();
    const lowP = pChar.toLowerCase();
    switch (pChar) {
      case lowP:
        return upP;
      case upP:
        return lowP;
      default:
        return pChar;
    }
  }

  /**
   * Méthode d'échappement de caractères en vue de placer une chaîne de caractère entre quotes.
   * Utilisé par la méthode StringUtils.quote
   * @param pString caractère à éventuellement échapper
   */
  private static _quote(pString: string): string {
    switch (pString) {
      case '\\':
        return '\\\\';
      case '\r':
        return '\\r';
      case '\n':
        return '\\n';
      case '"':
        return '\\"';
      default:
        return '';
    }
  }

  /**
   * Méthode d'échappement de caractères pour la intégration dans une expression régulière.
   * Utilisé par le datefield
   * @param pString caractère à éventuellement échapper
   */
  public static escapeForRegexp(pString: string): string {
    switch (pString) {
      case '/':
        return '\/';
      case '\\':
        return '\\\\';
      case '.':
        return '\\.';
      default:
        return pString;
    }
  }

  /**
   * collecte de la partie number d'une mesure (suppression px et %)
   */
  public static measureToNumber(pString: string): number {
    return Number(pString.replace('px', '').replace('%', ''));
  }


}
